import { Observer } from '@playcanvas/observer';
import { Container, Spinner } from '@playcanvas/pcui/react';
import React from 'react';
import ReactDOM from 'react-dom';

import { ObserverData } from '../types';
import { t } from '../translation';
import { buildAuthHeaders } from '../helpers';
import ErrorBox from './errors';
import LeftPanel from './left-panel';
import LoadControls from './load-controls';
import PopupPanel from './popup-panel';
import SelectedNode from './selected-node';
import LoadingBar from './components/loading-bar';
import RightPanel from './components/right-panel';
import { clampPan as clampPanUtil, normalizeWheelDelta, ZOOM_MIN, ZOOM_MAX } from './wheel-zoom-utils';
import FallbackGallery from './components/fallback-gallery';
import { GalleryPhoto } from '../transform-cameras';
import { version as appVersion } from '../../package.json';

interface CameraPhotoMarker {
    id: string;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
}

interface CameraDamageNote {
    id: string;
    dateTime: string;
    author: string;
    message: string;
}

interface CameraDamageEntry {
    id: string;
    name: string;
    description: string;
    selectValue: string;
    notes?: CameraDamageNote[];
}

interface AppState extends ObserverData {
    galleryOpen: boolean;
    cameraPositionsVisible: boolean;
    cameraPhotoOpen: boolean;
    cameraPhotoUrl: string;
    cameraPhotoId: string;
    cameraPhotoCarousel: Array<{ cameraId: string; imageUrl: string; imageName?: string }>;
    cameraPhotoIndex: number;
    cameraPhotoMode: 'all' | 'damage';
    cameraDamageMode: 'view' | 'select' | 'new' | 'existing' | 'confirmDelete' | 'notes';
    cameraDamageList: CameraDamageEntry[];
    cameraDamageSelectedId: string;
    cameraDamageCarouselIndex: number;
    cameraDamageForm: {
        name: string;
        description: string;
        selectValue: string;
    };
    cameraDamageCurrent: {
        id: string;
        name: string;
        description: string;
        selectValue: string;
    } | null;
    cameraDamageNotes: CameraDamageNote[];
    cameraDamageSelectedNoteId: string;
    cameraDamageNoteEditorMode: 'none' | 'new' | 'edit';
    cameraDamageNoteDeleteConfirmOpen: boolean;
    cameraDamageNoteForm: {
        dateTime: string;
        author: string;
        message: string;
    };
    cameraDamageOptions: Array<{
        id: string;
        name: string;
        description: string;
        selectValue: string;
    }>;
    selectedExistingDamageId: string;
    cameraPhotoInspectorOpen: boolean;
    cameraPhotoInspectorLoading: boolean;
    cameraPhotoZoom: number;
    cameraPhotoPan: { x: number; y: number };
    cameraPhotoNaturalWidth: number;
    cameraPhotoNaturalHeight: number;
    cameraPhotoMarkers: CameraPhotoMarker[];
    cameraPhotoDraftMarker: CameraPhotoMarker | null;
    pendingMarkerAfterDamage: CameraPhotoMarker | null;
    cameraPhotoCreateMode: boolean;
    cameraPhotoSelectedMarkerId: string;
    cameraPhotoEditMode: boolean;
    markerDamagePromptOpen: boolean;
    markerDamagePromptType: 'noDamage' | 'selectDamage';
    cameraDamageCenterModalOpen: boolean;
    damageMenuVisible: boolean;
    markerToDamageMap: Map<string, string>;
    fallbackGalleryOpen: boolean;
    fallbackGalleryPhotos: GalleryPhoto[];
    cameraPhotoLoadError: boolean;
}

class App extends React.Component<{ observer: Observer }, AppState> {

    canvasRef: any;
    inspectorViewportRef: React.RefObject<HTMLDivElement>;
    normalPhotoFrameRef: React.RefObject<HTMLDivElement>;
    normalPhotoImageRef: React.RefObject<HTMLImageElement>;
    private markerDragContext: {
        mode: 'pan' | 'move' | 'resize';
        startClientX: number;
        startClientY: number;
        startPan: { x: number; y: number };
        markerId: string;
        markerIsDraft: boolean;
        initialMarker: CameraPhotoMarker | null;
    } | null = null;
    private markerCollectionDirty = false;
    private requestPhotoUrlCache: Map<string, string> = new Map();
    private requestPhotoUrlCacheRequestId = '';
    private requestPhotoUrlCacheRetryPending = false;
    private markerLoadSeq = 0;
    private readonly CAMERA_MARKER_META_NAME = '__camera_markers__';

    constructor(props: any) {
        super(props);

        this.canvasRef = React.createRef();
        this.inspectorViewportRef = React.createRef<HTMLDivElement>();
        this.normalPhotoFrameRef = React.createRef<HTMLDivElement>();
        this.normalPhotoImageRef = React.createRef<HTMLImageElement>();
        const initialState = this._retrieveState();
        this.state = {
            ...(initialState || {}),
            galleryOpen: false,
            cameraPositionsVisible: false,
            cameraPhotoOpen: false,
            cameraPhotoUrl: '',
            cameraPhotoId: '',
            cameraPhotoCarousel: [],
            cameraPhotoIndex: 0,
            cameraPhotoMode: 'all',
            cameraDamageMode: 'view',
            cameraDamageList: [],
            cameraDamageSelectedId: '',
            cameraDamageCarouselIndex: 0,
            cameraDamageForm: {
                name: '',
                description: '',
                selectValue: ''
            },
            cameraDamageCurrent: null,
            cameraDamageNotes: [],
            cameraDamageSelectedNoteId: '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            cameraDamageOptions: [],
            selectedExistingDamageId: '',
            cameraPhotoInspectorOpen: false,
            cameraPhotoInspectorLoading: false,
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoNaturalWidth: 0,
            cameraPhotoNaturalHeight: 0,
            cameraPhotoMarkers: [],
            cameraPhotoDraftMarker: null,
            pendingMarkerAfterDamage: null,
            cameraPhotoCreateMode: false,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            markerDamagePromptOpen: false,
            markerDamagePromptType: 'noDamage',
            cameraDamageCenterModalOpen: false,
            damageMenuVisible: false,
            markerToDamageMap: new Map(),
            fallbackGalleryOpen: false,
            fallbackGalleryPhotos: [],
            cameraPhotoLoadError: false
        } as AppState;

        props.observer.on('*:set', () => {
            // update the state
            this.setState({
                ...this._retrieveState(),
                galleryOpen: this.state.galleryOpen,
                cameraPositionsVisible: this.state.cameraPositionsVisible,
                cameraPhotoOpen: this.state.cameraPhotoOpen,
                cameraPhotoUrl: this.state.cameraPhotoUrl,
                cameraPhotoId: this.state.cameraPhotoId,
                cameraPhotoCarousel: this.state.cameraPhotoCarousel,
                cameraPhotoIndex: this.state.cameraPhotoIndex,
                cameraPhotoMode: this.state.cameraPhotoMode,
                cameraDamageMode: this.state.cameraDamageMode,
                cameraDamageList: this.state.cameraDamageList,
                cameraDamageSelectedId: this.state.cameraDamageSelectedId,
                cameraDamageCarouselIndex: this.state.cameraDamageCarouselIndex,
                cameraDamageForm: this.state.cameraDamageForm,
                cameraDamageCurrent: this.state.cameraDamageCurrent,
                cameraDamageNotes: this.state.cameraDamageNotes,
                cameraDamageSelectedNoteId: this.state.cameraDamageSelectedNoteId,
                cameraDamageNoteEditorMode: this.state.cameraDamageNoteEditorMode,
                cameraDamageNoteDeleteConfirmOpen: this.state.cameraDamageNoteDeleteConfirmOpen,
                cameraDamageNoteForm: this.state.cameraDamageNoteForm,
                cameraDamageOptions: this.state.cameraDamageOptions,
                selectedExistingDamageId: this.state.selectedExistingDamageId,
                cameraPhotoInspectorOpen: this.state.cameraPhotoInspectorOpen,
                cameraPhotoInspectorLoading: this.state.cameraPhotoInspectorLoading,
                cameraPhotoZoom: this.state.cameraPhotoZoom,
                cameraPhotoPan: this.state.cameraPhotoPan,
                cameraPhotoNaturalWidth: this.state.cameraPhotoNaturalWidth,
                cameraPhotoNaturalHeight: this.state.cameraPhotoNaturalHeight,
                cameraPhotoMarkers: this.state.cameraPhotoMarkers,
                cameraPhotoDraftMarker: this.state.cameraPhotoDraftMarker,
                pendingMarkerAfterDamage: this.state.pendingMarkerAfterDamage,
                cameraPhotoCreateMode: this.state.cameraPhotoCreateMode,
                cameraPhotoSelectedMarkerId: this.state.cameraPhotoSelectedMarkerId,
                cameraPhotoEditMode: this.state.cameraPhotoEditMode,
                markerDamagePromptOpen: this.state.markerDamagePromptOpen,
                markerDamagePromptType: this.state.markerDamagePromptType,
                cameraDamageCenterModalOpen: this.state.cameraDamageCenterModalOpen,
                damageMenuVisible: this.state.damageMenuVisible,
                markerToDamageMap: this.state.markerToDamageMap,
                fallbackGalleryOpen: this.state.fallbackGalleryOpen,
                fallbackGalleryPhotos: this.state.fallbackGalleryPhotos,
                cameraPhotoLoadError: this.state.cameraPhotoLoadError
            } as AppState);
        });
    }

    componentDidMount() {
        (window as any).openCameraPhotoPopup = this.openCameraPhotoPopup;
        (window as any).openDamageCameraCarousel = this.openDamageCameraCarousel;
        (window as any).openFallbackGallery = (photos: GalleryPhoto[]) => {
            this.setState({ fallbackGalleryOpen: true, fallbackGalleryPhotos: photos });
        };
        document.addEventListener('mousemove', this.handleInspectorMouseMove);
        document.addEventListener('mouseup', this.handleInspectorMouseUp);
        this.attachWheelListener();
    }

    componentDidUpdate(prevProps: Readonly<{ observer: Observer }>, prevState: Readonly<AppState>) {
        if (prevState.cameraPhotoId !== this.state.cameraPhotoId && this.state.cameraPhotoId) {
            if (this.state.cameraPhotoInspectorOpen) {
                this.setState({
                    cameraPhotoZoom: 1,
                    cameraPhotoPan: { x: 0, y: 0 },
                    cameraPhotoDraftMarker: null,
                    cameraPhotoSelectedMarkerId: '',
                    cameraPhotoCreateMode: false,
                    cameraPhotoEditMode: false
                });
            }
            this.loadCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId);
        }

        if (!prevState.cameraPhotoInspectorOpen && this.state.cameraPhotoInspectorOpen) {
            this.attachWheelListener();
        }
        if (prevState.cameraPhotoInspectorOpen && !this.state.cameraPhotoInspectorOpen) {
            this.detachWheelListener();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleInspectorMouseMove);
        document.removeEventListener('mouseup', this.handleInspectorMouseUp);
        this.detachWheelListener();
    }

    _retrieveState = () => {
        const state: any = {};
        (this.props.observer as any)._keys.forEach((key: string) => {
            state[key] = this.props.observer.get(key);
        });
        return state;
    };

    _setStateProperty = (path: string, value: string) => {
        this.props.observer.set(path, value);
    };

    toggleGallery = () => {
        this.setState(prevState => ({ galleryOpen: !prevState.galleryOpen }));
    };

    private buildDefaultNoteDateTime = (): string => {
        const now = new Date();
        const pad = (value: number) => String(value).padStart(2, '0');
        const year = now.getFullYear();
        const month = pad(now.getMonth() + 1);
        const day = pad(now.getDate());
        const hours = pad(now.getHours());
        const minutes = pad(now.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    private formatNoteDateTimeForInput = (value: string): string => {
        if (!value) {
            return this.buildDefaultNoteDateTime();
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value.slice(0, 16);
        }

        const pad = (raw: number) => String(raw).padStart(2, '0');
        const year = parsed.getFullYear();
        const month = pad(parsed.getMonth() + 1);
        const day = pad(parsed.getDate());
        const hours = pad(parsed.getHours());
        const minutes = pad(parsed.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    private formatNoteDateTimeForList = (value: string): string => {
        if (!value) {
            return '-';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }

        return parsed.toLocaleString();
    };

    private normalizeCameraDamageNotes = (notes: unknown): CameraDamageNote[] => {
        if (!Array.isArray(notes)) {
            return [];
        }

        return notes
            .map((entry, index) => {
                const id = typeof (entry as any)?.id === 'string' ? (entry as any).id.trim() : '';
                const dateTime = typeof (entry as any)?.dateTime === 'string' ? (entry as any).dateTime.trim() : '';
                const author = typeof (entry as any)?.author === 'string' ? (entry as any).author.trim() : '';
                const message = typeof (entry as any)?.message === 'string' ? (entry as any).message.trim() : '';

                if (!author || !message) {
                    return null;
                }

                return {
                    id: id || `ui-note-${index}`,
                    dateTime,
                    author,
                    message
                };
            })
            .filter((note): note is CameraDamageNote => Boolean(note));
    };

    private syncDamageNavigationFilter = (mode: 'all' | 'damage', cameraIds: string[]) => {
        if (!window.viewer) {
            return;
        }

        if (mode === 'damage' && cameraIds.length > 0) {
            window.viewer.setCameraDamageNavigationFilter?.(cameraIds);
            return;
        }

        window.viewer.clearCameraDamageNavigationFilter?.();
    };

    private persistDamagesAfterConfirm = async (): Promise<void> => {
        if (!window.viewer?.updateDamagesInAPI) {
            return;
        }

        await window.viewer.updateDamagesInAPI();
    };

    private openDamageModeFromMarkerFlow = (mode: 'select' | 'new') => {
        const updates: any = {
            markerDamagePromptOpen: false,
            cameraDamageCenterModalOpen: true,
            cameraDamageMode: mode,
            cameraPhotoCreateMode: false,
            cameraPhotoInspectorOpen: false,
            cameraPhotoInspectorLoading: false,
            pendingMarkerAfterDamage: this.state.pendingMarkerAfterDamage || this.state.cameraPhotoDraftMarker
        };

        // When creating a new damage, clear the form and deselect so saveCameraDamage creates instead of edits
        if (mode === 'new') {
            updates.cameraDamageSelectedId = '';
            updates.cameraDamageForm = {
                name: '',
                description: '',
                selectValue: ''
            };
        }

        this.setState(updates);
    };

    private closeMarkerPrompt = () => {
        this.setState({
            markerDamagePromptOpen: false,
            cameraPhotoDraftMarker: null,
            pendingMarkerAfterDamage: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            cameraPhotoCreateMode: false
        });
    };

    private closeDamageCenterModal = () => {
        this.setState({
            cameraDamageCenterModalOpen: false,
            cameraDamageMode: 'view'
        });
    };

    private commitDraftMarkerIfReady = async () => {
        const draft = this.state.cameraPhotoDraftMarker || this.state.pendingMarkerAfterDamage;
        if (!draft || !this.state.cameraPhotoId || !this.state.cameraDamageSelectedId) {
            return;
        }

        // Only collect markers that belong to the currently selected damage
        const existingMarkersForDamage = this.state.cameraPhotoMarkers.filter((entry) => {
            return this.state.markerToDamageMap.get(entry.id) === this.state.cameraDamageSelectedId;
        });
        const damageMarkers = [
            ...existingMarkersForDamage.filter((entry) => entry.id !== draft.id),
            draft
        ];

        this.setState({
            cameraPhotoDraftMarker: null,
            pendingMarkerAfterDamage: null,
            cameraPhotoEditMode: false,
            cameraDamageCenterModalOpen: false,
            markerDamagePromptOpen: false,
            cameraDamageMode: 'view'
        });

        // Persist only the markers for this specific damage
        await this.persistCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId, damageMarkers);

        // Reload all markers from storage to rebuild markerToDamageMap correctly
        await this.loadAllCameraPhotoMarkers(this.state.cameraPhotoId);
    };

    private updateCameraDamageContext = (cameraId: string, selectedDamageId = '') => {
        const context = window.viewer?.getCameraDamageContext?.(cameraId, selectedDamageId);
        const damages = Array.isArray(context?.damages) ? context.damages : [];
        const current = context?.current || null;
        const notes = this.normalizeCameraDamageNotes((current as any)?.notes);
        const currentIndex = current
            ? Math.max(0, damages.findIndex((entry: CameraDamageEntry) => entry.id === current.id))
            : damages.length;

        return {
            current,
            damages,
            selectedDamageId: current?.id || '',
            carouselIndex: currentIndex,
            options: context?.options || [],
            form: {
                name: current?.name || '',
                description: current?.description || '',
                selectValue: current?.selectValue || ''
            },
            notes
        };
    };

    private setDamageCarouselIndex = (index: number) => {
        const list = this.state.cameraDamageList;
        if (!list.length) {
            this.setState({
                cameraDamageCarouselIndex: 0,
                cameraDamageCurrent: null,
                cameraDamageSelectedId: '',
                cameraDamageNotes: [],
                cameraDamageSelectedNoteId: '',
                cameraDamageNoteEditorMode: 'none',
                cameraDamageNoteDeleteConfirmOpen: false,
                cameraDamageNoteForm: {
                    dateTime: '',
                    author: '',
                    message: ''
                },
                cameraDamageForm: {
                    name: '',
                    description: '',
                    selectValue: ''
                }
            }, () => {
                this.loadCameraPhotoMarkers(this.state.cameraPhotoId, '');
            });
            return;
        }

        const normalized = ((index % list.length) + list.length) % list.length;

        const selectedDamage = list[normalized];
        const notes = this.normalizeCameraDamageNotes((selectedDamage as any)?.notes);

        // Find the first marker belonging to this damage to highlight it
        let highlightMarkerId = '';
        for (const [markerId, damageId] of this.state.markerToDamageMap.entries()) {
            if (damageId === selectedDamage.id) {
                highlightMarkerId = markerId;
                break;
            }
        }

        this.setState({
            cameraDamageCarouselIndex: normalized,
            cameraDamageCurrent: selectedDamage,
            cameraDamageSelectedId: selectedDamage.id,
            cameraDamageForm: {
                name: selectedDamage.name || '',
                description: selectedDamage.description || '',
                selectValue: selectedDamage.selectValue || ''
            },
            cameraDamageNotes: notes,
            cameraDamageSelectedNoteId: notes[0]?.id || '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            cameraDamageMode: 'view',
            cameraPhotoSelectedMarkerId: highlightMarkerId
        });
    };

    private setCameraCarouselIndex = (index: number) => {
        const selected = this.state.cameraPhotoCarousel[index];
        if (!selected) {
            return;
        }

        const context = this.updateCameraDamageContext(selected.cameraId, this.state.cameraDamageSelectedId);
        const hasDamages = context.damages.length > 0;
        this.setState({
            cameraPhotoIndex: index,
            cameraPhotoId: selected.cameraId,
            cameraPhotoUrl: selected.imageUrl,
            cameraPhotoLoadError: false,
            cameraDamageList: context.damages,
            cameraDamageCurrent: context.current,
            cameraDamageSelectedId: context.selectedDamageId,
            cameraDamageCarouselIndex: context.carouselIndex,
            cameraDamageOptions: context.options,
            cameraDamageForm: context.form,
            cameraDamageNotes: context.notes,
            cameraDamageSelectedNoteId: context.notes[0]?.id || '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            selectedExistingDamageId: '',
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoDraftMarker: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            cameraPhotoCreateMode: false,
            damageMenuVisible: hasDamages ? this.state.damageMenuVisible : false
        }, () => {
            this.resolveAndSetBestCameraPhotoUrl(selected.cameraId, selected.imageUrl, selected.imageName || '');
            this.loadCameraPhotoMarkers(selected.cameraId, context.selectedDamageId);
            const carouselIds = this.state.cameraPhotoCarousel.map((entry) => entry.cameraId);
            this.syncDamageNavigationFilter(this.state.cameraPhotoMode, carouselIds);
        });
    };

    openCameraPhotoPopup = (
        cameraId: string,
        imageUrl: string,
        carouselCameraIds: string[] | null = null,
        mode: 'all' | 'damage' = 'all',
        damageId = ''
    ) => {
        const viewerEntries: Array<{ cameraId: string; imageUrl: string; imageName?: string }> =
            window.viewer?.getCameraPhotoEntries?.(carouselCameraIds || undefined) || [];
        const fallbackEntry: Array<{ cameraId: string; imageUrl: string; imageName?: string }> =
            (cameraId && imageUrl) ? [{ cameraId, imageUrl, imageName: '' }] : [];
        const carousel = viewerEntries.length ? viewerEntries : fallbackEntry;

        if (!carousel.length) {
            return;
        }

        const targetIndex = mode === 'damage'
            ? 0
            : Math.max(0, carousel.findIndex((entry) => entry.cameraId === cameraId));

        const selected = carousel[targetIndex] || carousel[0];
        const context = this.updateCameraDamageContext(selected.cameraId, damageId);

        // When opening from camera click (not damage eye icon), don't pre-select any damage
        // so no markers are highlighted. When opening from damage navigation, select the damage
        // and open the menu.
        const isDamageNavigation = mode === 'damage' && !!damageId;

        this.setState({
            cameraPhotoOpen: true,
            cameraPhotoCarousel: carousel,
            cameraPhotoIndex: targetIndex,
            cameraPhotoMode: mode,
            cameraPhotoUrl: selected.imageUrl,
            cameraPhotoId: selected.cameraId,
            cameraPhotoLoadError: false,
            cameraDamageList: context.damages,
            cameraDamageCurrent: isDamageNavigation ? context.current : null,
            cameraDamageSelectedId: isDamageNavigation ? context.selectedDamageId : '',
            cameraDamageCarouselIndex: isDamageNavigation ? context.carouselIndex : 0,
            cameraDamageOptions: context.options,
            cameraDamageNotes: isDamageNavigation ? context.notes : [],
            cameraDamageSelectedNoteId: isDamageNavigation ? (context.notes[0]?.id || '') : '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            cameraDamageMode: 'view',
            cameraDamageCenterModalOpen: false,
            markerDamagePromptOpen: false,
            selectedExistingDamageId: '',
            cameraDamageForm: isDamageNavigation ? context.form : { name: '', description: '', selectValue: '' },
            damageMenuVisible: isDamageNavigation
        }, () => {
            if (window.viewer) {
                window.viewer.setCameraOverlayOpen?.(true);
            }
            this.resolveAndSetBestCameraPhotoUrl(selected.cameraId, selected.imageUrl, selected.imageName || '');
            this.loadCameraPhotoMarkers(selected.cameraId, context.selectedDamageId);
            const carouselIds = this.state.cameraPhotoCarousel.map((entry) => entry.cameraId);
            this.syncDamageNavigationFilter(this.state.cameraPhotoMode, carouselIds);
        });
    };

    openDamageCameraCarousel = (cameraIds: string[], damageId = '') => {
        if (!Array.isArray(cameraIds) || cameraIds.length === 0) {
            return;
        }

        this.openCameraPhotoPopup(cameraIds[0], '', cameraIds, 'damage', damageId);
    };

    private navigateCameraPhotoByDirection = async (
        direction: 'left' | 'right' | 'up' | 'down',
        fallbackOffset: number
    ) => {
        await this.flushCurrentCameraPhotoMarkers();

        const total = this.state.cameraPhotoCarousel.length;
        if (!total) {
            return;
        }

        if (this.state.cameraPhotoMode === 'all' && window.viewer && this.state.cameraPhotoId) {
            const visibleCameraIds = this.state.cameraPhotoCarousel.map((entry) => entry.cameraId);
            const directionalEntry = window.viewer.getClosestCameraPhotoEntryInDirection?.(
                this.state.cameraPhotoId,
                direction,
                visibleCameraIds
            );

            if (directionalEntry) {
                const targetIndex = this.state.cameraPhotoCarousel.findIndex(
                    (entry) => entry.cameraId === directionalEntry.cameraId
                );

                if (targetIndex >= 0) {
                    this.setCameraCarouselIndex(targetIndex);
                    return;
                }
            }
        }

        if (!fallbackOffset) {
            return;
        }

        const fallbackIndex = (this.state.cameraPhotoIndex + fallbackOffset + total) % total;
        this.setCameraCarouselIndex(fallbackIndex);
    };

    goToPreviousCameraPhoto = async () => {
        await this.navigateCameraPhotoByDirection('left', -1);
    };

    goToNextCameraPhoto = async () => {
        await this.navigateCameraPhotoByDirection('right', 1);
    };

    goToUpperCameraPhoto = async () => {
        await this.navigateCameraPhotoByDirection('up', -1);
    };

    goToLowerCameraPhoto = async () => {
        await this.navigateCameraPhotoByDirection('down', 1);
    };

    closeCameraPhotoPopup = () => {
        this.setState({
            cameraPhotoOpen: false,
            cameraPhotoUrl: '',
            cameraPhotoId: '',
            cameraPhotoLoadError: false,
            cameraPhotoCarousel: [],
            cameraPhotoIndex: 0,
            cameraDamageMode: 'view',
            cameraDamageList: [],
            cameraDamageSelectedId: '',
            cameraDamageCarouselIndex: 0,
            cameraDamageCenterModalOpen: false,
            markerDamagePromptOpen: false,
            selectedExistingDamageId: '',
            cameraPhotoInspectorOpen: false,
            cameraPhotoInspectorLoading: false,
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoNaturalWidth: 0,
            cameraPhotoNaturalHeight: 0,
            cameraPhotoMarkers: [],
            cameraPhotoDraftMarker: null,
            pendingMarkerAfterDamage: null,
            cameraPhotoCreateMode: false,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            cameraDamageNotes: [],
            cameraDamageSelectedNoteId: '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            markerToDamageMap: new Map()
        });

        if (window.viewer) {
            window.viewer.clearCameraDamageNavigationFilter?.();
            window.viewer.setCameraOverlayOpen?.(false);
        }
    };

    refreshCameraDamageContext = (preferredDamageId = '', onDone?: () => void) => {
        if (!window.viewer || !this.state.cameraPhotoId) {
            return;
        }

        const selectedId = preferredDamageId || this.state.cameraDamageSelectedId;
        const context = this.updateCameraDamageContext(this.state.cameraPhotoId, selectedId);
        const hasPendingDraft = !!(this.state.pendingMarkerAfterDamage || this.state.cameraPhotoDraftMarker);
        this.setState({
            cameraDamageCurrent: context.current,
            cameraDamageList: context.damages,
            cameraDamageSelectedId: context.selectedDamageId,
            cameraDamageCarouselIndex: context.carouselIndex,
            cameraDamageOptions: context.options || [],
            cameraDamageForm: context.form,
            cameraDamageNotes: context.notes,
            cameraDamageSelectedNoteId: context.notes[0]?.id || '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            },
            selectedExistingDamageId: ''
        }, () => {
            // Skip marker reload when there's a pending draft — commitDraftMarkerIfReady
            // will persist and then reload markers to avoid a race condition where the
            // async load overwrites the draft's markerToDamageMap entry.
            if (!hasPendingDraft) {
                this.loadCameraPhotoMarkers(this.state.cameraPhotoId, context.selectedDamageId);
            }
            onDone?.();
        });
    };

    openAssociateDamage = () => {
        this.setState({
            cameraDamageMode: 'select',
            cameraDamageCarouselIndex: this.state.cameraDamageList.length,
            cameraDamageCurrent: null,
            cameraDamageSelectedId: '',
            selectedExistingDamageId: ''
        }, () => {
            this.loadCameraPhotoMarkers(this.state.cameraPhotoId, '');
        });
    };

    openDeleteCameraDamageConfirm = () => {
        if (!this.state.cameraDamageCurrent) {
            return;
        }

        this.setState({ cameraDamageMode: 'confirmDelete' });
    };

    handleDamageFieldChange = (field: 'name' | 'description' | 'selectValue', value: string) => {
        this.setState(prev => ({
            cameraDamageForm: {
                ...prev.cameraDamageForm,
                [field]: value
            }
        }));
    };

    saveCameraDamage = async () => {
        if (!window.viewer || !this.state.cameraPhotoId) {
            return;
        }

        const payload = this.state.cameraDamageForm;
        if (!payload.name.trim() || !payload.description.trim() || !payload.selectValue.trim()) {
            alert(t('markers.fillAllFields'));
            return;
        }

        const ok = window.viewer.createOrUpdateCameraDamage?.(
            this.state.cameraPhotoId,
            payload,
            this.state.cameraDamageSelectedId || undefined
        );
        if (!ok) {
            alert(t('api.errorSavingDamages'));
            return;
        }

        await this.persistDamagesAfterConfirm();

        const preferNewDamage = !this.state.cameraDamageSelectedId;
        let preferredId = this.state.cameraDamageSelectedId;
        if (preferNewDamage) {
            const latestContext = this.updateCameraDamageContext(this.state.cameraPhotoId);
            preferredId = latestContext.damages[latestContext.damages.length - 1]?.id || '';
        }

        this.refreshCameraDamageContext(preferredId, () => {
            this.commitDraftMarkerIfReady();
        });
        this.setState({
            cameraDamageMode: 'view',
            cameraDamageCenterModalOpen: false
        });
    };

    assignExistingDamage = async () => {
        if (!window.viewer || !this.state.cameraPhotoId || !this.state.selectedExistingDamageId) {
            return;
        }

        const ok = window.viewer.assignExistingDamageToCamera?.(this.state.cameraPhotoId, this.state.selectedExistingDamageId);
        if (!ok) {
            alert(t('api.errorSavingDamages'));
            return;
        }

        await this.persistDamagesAfterConfirm();

        this.refreshCameraDamageContext(this.state.selectedExistingDamageId, () => {
            this.commitDraftMarkerIfReady();
        });
        this.setState({
            cameraDamageMode: 'view',
            cameraDamageCenterModalOpen: false
        });
    };

    confirmDeleteCameraDamage = async () => {
        if (!window.viewer || !this.state.cameraPhotoId || !this.state.cameraDamageCurrent) {
            return;
        }

        const ok = window.viewer.deleteCameraDamage?.(
            this.state.cameraPhotoId,
            this.state.cameraDamageSelectedId || undefined
        );
        if (!ok) {
            return;
        }

        await this.persistDamagesAfterConfirm();

        this.refreshCameraDamageContext();
        this.setState({
            cameraDamageMode: 'view',
            cameraDamageForm: {
                name: '',
                description: '',
                selectValue: ''
            }
        });
    };

    openCameraDamageNotes = () => {
        const notes = this.state.cameraDamageNotes;
        this.setState({
            cameraDamageMode: 'notes',
            cameraDamageSelectedNoteId: notes[0]?.id || '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            }
        });
    };

    exitCameraDamageNotes = () => {
        this.setState({
            cameraDamageMode: 'view',
            cameraDamageSelectedNoteId: '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            }
        });
    };

    startNewCameraDamageNote = () => {
        this.setState({
            cameraDamageNoteEditorMode: 'new',
            cameraDamageSelectedNoteId: '',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: this.buildDefaultNoteDateTime(),
                author: '',
                message: ''
            }
        });
    };

    startEditCameraDamageNote = () => {
        const selected = this.state.cameraDamageNotes.find((note) => note.id === this.state.cameraDamageSelectedNoteId);
        if (!selected) {
            return;
        }

        this.setState({
            cameraDamageNoteEditorMode: 'edit',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: this.formatNoteDateTimeForInput(selected.dateTime),
                author: selected.author || '',
                message: selected.message || ''
            }
        });
    };

    handleCameraDamageNoteFieldChange = (field: 'dateTime' | 'author' | 'message', value: string) => {
        this.setState((prev) => ({
            cameraDamageNoteForm: {
                ...prev.cameraDamageNoteForm,
                [field]: value
            }
        }));
    };

    cancelCameraDamageNoteEdition = () => {
        this.setState({
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            }
        });
    };

    openDeleteCameraDamageNoteConfirm = () => {
        if (!this.state.cameraDamageSelectedNoteId) {
            return;
        }

        this.setState({ cameraDamageNoteDeleteConfirmOpen: true });
    };

    cancelDeleteCameraDamageNoteConfirm = () => {
        this.setState({ cameraDamageNoteDeleteConfirmOpen: false });
    };

    saveCameraDamageNote = async () => {
        if (!window.viewer || !this.state.cameraPhotoId) {
            return;
        }

        const payload = this.state.cameraDamageNoteForm;
        if (!payload.author.trim() || !payload.message.trim()) {
            alert(t('markers.fillAllFields'));
            return;
        }

        let ok = false;
        if (this.state.cameraDamageNoteEditorMode === 'edit') {
            if (!this.state.cameraDamageSelectedNoteId) {
                return;
            }

            ok = window.viewer.updateCameraDamageNote?.(
                this.state.cameraPhotoId,
                this.state.cameraDamageSelectedId || undefined,
                this.state.cameraDamageSelectedNoteId,
                payload
            );
        } else {
            ok = window.viewer.addCameraDamageNote?.(
                this.state.cameraPhotoId,
                this.state.cameraDamageSelectedId || undefined,
                payload
            );
        }

        if (!ok) {
            alert(t('api.errorSavingDamages'));
            return;
        }

        await this.persistDamagesAfterConfirm();

        this.refreshCameraDamageContext();
        this.setState({
            cameraDamageMode: 'notes',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            }
        });
    };

    confirmDeleteCameraDamageNote = async () => {
        if (!window.viewer || !this.state.cameraPhotoId || !this.state.cameraDamageSelectedNoteId) {
            return;
        }

        const ok = window.viewer.deleteCameraDamageNote?.(
            this.state.cameraPhotoId,
            this.state.cameraDamageSelectedId || undefined,
            this.state.cameraDamageSelectedNoteId
        );
        if (!ok) {
            return;
        }

        await this.persistDamagesAfterConfirm();

        this.refreshCameraDamageContext();
        this.setState({
            cameraDamageMode: 'notes',
            cameraDamageSelectedNoteId: '',
            cameraDamageNoteEditorMode: 'none',
            cameraDamageNoteDeleteConfirmOpen: false,
            cameraDamageNoteForm: {
                dateTime: '',
                author: '',
                message: ''
            }
        });
    };

    toggleCameraPositions = () => {
        this.setState(prevState => {
            const next = !prevState.cameraPositionsVisible;
            if (window.viewer) {
                window.viewer.toggleCameraPositionsLayer(next);
            }
            return { cameraPositionsVisible: next };
        });
    };

    getRequestId = (): string => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('requestID') || '';
    };

    getApiConfig = () => {
        // Get API configuration from viewer if available
        if (window.viewer) {
            return {
                apiBaseUrl: window.viewer.API_BASE_URL,
                apiStage: window.viewer.API_STAGE_PATH,
                apiPrefix: window.viewer.API_PREFIX
            };
        }

        const urlParams = new URLSearchParams(window.location.search);
        const loadParamRaw = urlParams.get('load') || '';
        let loadParam = loadParamRaw;

        try {
            loadParam = decodeURIComponent(loadParamRaw);
        } catch {
            // Keep original value if decode fails.
        }

        const normalizedLoadUrl = loadParam.toLowerCase();
        const isProdFromLoad = normalizedLoadUrl.includes('pro-mobius');
        const isPreFromLoad = normalizedLoadUrl.includes('pre-mobius');
        const isDevOrigin = window.location.origin === 'https://dev.3dexpertise-vw.grupomobius.com' ||
            window.location.search.includes('stageUrlDev');
        const useProd = isProdFromLoad || (!isPreFromLoad && !isDevOrigin);

        // Default fallback
        return {
            apiBaseUrl: useProd
                ? 'https://8i705rlrck.execute-api.eu-west-1.amazonaws.com'
                : 'https://s0ixjydkk6.execute-api.eu-west-1.amazonaws.com',
            apiStage: useProd ? 'pro' : 'pre',
            apiPrefix: 'api'
        };
    };

    private normalizePhotoKey = (value: string): string => {
        return String(value || '').trim().toLowerCase();
    };

    private stripPhotoExtension = (value: string): string => {
        return String(value || '').replace(/\.[^.]+$/, '');
    };

    private extractFilenameWithoutExtension = (urlValue: string): string => {
        try {
            const parsed = new URL(urlValue);
            const lastSegment = decodeURIComponent(parsed.pathname.split('/').pop() || '');
            return this.stripPhotoExtension(lastSegment);
        } catch {
            const lastSegment = decodeURIComponent(String(urlValue).split('/').pop() || '');
            return this.stripPhotoExtension(lastSegment);
        }
    };

    private cacheRequestPhotoUrl = (key: string, url: string) => {
        const normalized = this.normalizePhotoKey(key);
        if (!normalized || !url) {
            return;
        }
        this.requestPhotoUrlCache.set(normalized, url);
        this.requestPhotoUrlCache.set(this.normalizePhotoKey(this.stripPhotoExtension(normalized)), url);
    };

    private loadRequestPhotoUrlCache = async () => {
        const requestId = this.getRequestId();
        if (!requestId) {
            return;
        }

        if (this.requestPhotoUrlCacheRequestId === requestId && this.requestPhotoUrlCache.size > 0) {
            return;
        }

        const { apiBaseUrl, apiStage, apiPrefix } = this.getApiConfig();
        const url = `${apiBaseUrl}/${apiStage}/${apiPrefix}/requests/${encodeURIComponent(requestId)}/photos?type=map`;

        try {
            const response = await fetch(url, { headers: buildAuthHeaders() });
            if (!response.ok) {
                throw new Error(`Photo map API returned ${response.status}`);
            }

            const payload = await response.json();
            const freshCache = new Map<string, string>();

            if (Array.isArray(payload?.media)) {
                payload.media.forEach((media: any) => {
                    if (!Array.isArray(media?.frames)) {
                        return;
                    }

                    media.frames.forEach((frame: any) => {
                        const frameUrl = String(frame?.url || '').trim();
                        if (!frameUrl) {
                            return;
                        }

                        const frameName = String(frame?.frameName || '').trim();
                        const frameNum = String(frame?.frameNum ?? '').trim();
                        const filenameKey = this.extractFilenameWithoutExtension(frameUrl);

                        const setKey = (k: string) => {
                            const normalized = this.normalizePhotoKey(k);
                            if (!normalized) {
                                return;
                            }
                            freshCache.set(normalized, frameUrl);
                            freshCache.set(this.normalizePhotoKey(this.stripPhotoExtension(normalized)), frameUrl);
                        };

                        setKey(frameName);
                        setKey(frameNum);
                        setKey(filenameKey);
                    });
                });
            }

            this.requestPhotoUrlCache = freshCache;
        } catch (error) {
            if (!this.requestPhotoUrlCacheRetryPending) {
                this.requestPhotoUrlCacheRetryPending = true;
                setTimeout(async () => {
                    try {
                        this.requestPhotoUrlCacheRequestId = '';
                        await this.loadRequestPhotoUrlCache();
                    } catch (retryError) {
                        console.warn('[CameraPhoto] Retry also failed. Accepting failure gracefully.', retryError);
                    } finally {
                        this.requestPhotoUrlCacheRetryPending = false;
                    }
                }, 10000);
            }
            throw error;
        } finally {
            this.requestPhotoUrlCacheRequestId = requestId;
        }
    };

    private resolveAndSetBestCameraPhotoUrl = async (cameraId: string, fallbackUrl: string, imageName = '') => {
        if (!cameraId || !fallbackUrl) {
            return;
        }

        const currentCameraId = cameraId;
        const fallbackFilename = this.extractFilenameWithoutExtension(fallbackUrl);

        let bestUrl = fallbackUrl;

        try {
            await this.loadRequestPhotoUrlCache();

            const candidates = [
                imageName,
                this.stripPhotoExtension(imageName),
                fallbackFilename,
                cameraId,
                this.stripPhotoExtension(cameraId)
            ];

            for (const candidate of candidates) {
                const normalized = this.normalizePhotoKey(candidate);
                if (!normalized) {
                    continue;
                }

                const mapped = this.requestPhotoUrlCache.get(normalized);
                if (mapped) {
                    bestUrl = mapped;
                    break;
                }
            }
        } catch (error) {
            console.warn('[CameraPhoto] Could not resolve full-resolution URL. Using fallback source.', error);
        }

        if (this.state.cameraPhotoId !== currentCameraId) {
            return;
        }

        if (bestUrl !== this.state.cameraPhotoUrl) {
            this.setState((prev) => ({
                cameraPhotoUrl: bestUrl,
                cameraPhotoCarousel: prev.cameraPhotoCarousel.map((entry) => {
                    return entry.cameraId === currentCameraId
                        ? { ...entry, imageUrl: bestUrl }
                        : entry;
                })
            }));
        }
    };

    downloadCurrentCameraPhoto = async () => {
        if (!this.state.cameraPhotoUrl) {
            return;
        }

        const fallbackName = this.state.cameraPhotoId || `camera-photo-${Date.now()}`;
        const markers = this.state.cameraPhotoMarkers || [];

        try {
            const response = await fetch(this.state.cameraPhotoUrl);
            if (!response.ok) {
                throw new Error(`Unable to download image. Status: ${response.status}`);
            }
            const blob = await response.blob();

            // If there are markers, compose them onto the image using canvas
            if (markers.length > 0) {
                const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                    const image = new Image();
                    image.crossOrigin = 'anonymous';
                    image.onload = () => resolve(image);
                    image.onerror = () => reject(new Error('Image load failed'));
                    image.src = URL.createObjectURL(blob);
                });

                const { naturalWidth, naturalHeight } = img;
                const canvas = document.createElement('canvas');
                canvas.width = naturalWidth;
                canvas.height = naturalHeight;
                const ctx = canvas.getContext('2d');

                if (ctx) {
                    ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);

                    const borderWidth = Math.max(3, Math.round(Math.max(naturalWidth, naturalHeight) / 500));
                    const selectedDamageId = this.state.cameraDamageSelectedId || '';

                    for (const marker of markers) {
                        const damageId = this.state.markerToDamageMap.get(marker.id) || '';
                        const isSelected = damageId === selectedDamageId && selectedDamageId !== '';
                        const color = isSelected ? '#26a89e' : 'rgba(255, 89, 122, 0.95)';

                        const x = marker.centerX - marker.width * 0.5;
                        const y = marker.centerY - marker.height * 0.5;
                        ctx.strokeStyle = color;
                        ctx.lineWidth = borderWidth;
                        ctx.strokeRect(x, y, marker.width, marker.height);
                    }

                    const pngBlob = await new Promise<Blob | null>((resolve) => {
                        canvas.toBlob((b) => resolve(b), 'image/png');
                    });

                    URL.revokeObjectURL(img.src);

                    if (pngBlob) {
                        const blobUrl = URL.createObjectURL(pngBlob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = `${fallbackName}_damage.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                        return;
                    }
                }

                URL.revokeObjectURL(img.src);
            }

            // No markers or canvas failed — download original
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const extension = blob.type.includes('png') ? 'png' : 'jpg';
            link.href = blobUrl;
            link.download = `${fallbackName}.${extension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Unable to download camera photo locally.', error);
            alert(t('gallery.downloadError'));
        }
    };

    private getCameraMarkerStorageKey = (cameraId: string, damageId: string): string => {
        const requestId = this.getRequestId() || 'no-request';
        return `camera-photo-markers-${requestId}-${cameraId}-${damageId || 'unassigned'}`;
    };

    private getDamagesEndpoint = (): string | null => {
        const requestId = this.getRequestId();
        if (!requestId) {
            return null;
        }

        const { apiBaseUrl, apiStage, apiPrefix } = this.getApiConfig();
        return `${apiBaseUrl}/${apiStage}/${apiPrefix}/damages/${encodeURIComponent(requestId)}`;
    };

    private isMarkerMetadataDamageEntry = (entry: any, cameraId?: string, damageId?: string): boolean => {
        if (!entry || typeof entry !== 'object') {
            return false;
        }

        const isMeta = entry.markerMetadata === true || entry.name === this.CAMERA_MARKER_META_NAME;
        if (!isMeta) {
            return false;
        }

        if (!cameraId) {
            return true;
        }

        const sameCamera = entry.cameraId === cameraId;
        if (!sameCamera) {
            return false;
        }

        if (!damageId) {
            return true;
        }

        return entry.damageId === damageId;
    };

    private extractCameraMarkersFromDamageList = (damageList: any[], cameraId: string, damageId: string): CameraPhotoMarker[] | null => {
        if (!Array.isArray(damageList)) {
            return null;
        }

        const markerEntry = damageList.find((entry) => this.isMarkerMetadataDamageEntry(entry, cameraId, damageId));
        const legacyMarkerEntry = markerEntry
            ? null
            : damageList.find((entry) => this.isMarkerMetadataDamageEntry(entry, cameraId) && !entry?.damageId);

        const source = markerEntry || legacyMarkerEntry;
        if (!source) {
            return null;
        }

        return this.normalizeCameraMarkerCollection(source.imageMarkers || source.cameraMarkers || []);
    };

    private upsertCameraMarkerMetadataInDamageList = (
        damageList: any[],
        cameraId: string,
        damageId: string,
        markers: CameraPhotoMarker[]
    ): any[] => {
        const roundedMarkers = markers.map((entry) => ({
            id: entry.id,
            centerX: Math.round(entry.centerX),
            centerY: Math.round(entry.centerY),
            width: Math.round(entry.width),
            height: Math.round(entry.height)
        }));

        let updated = false;
        const nextList = (Array.isArray(damageList) ? damageList : []).map((entry) => {
            if (!this.isMarkerMetadataDamageEntry(entry, cameraId, damageId)) {
                return entry;
            }

            updated = true;
            return {
                name: this.CAMERA_MARKER_META_NAME,
                markerMetadata: true,
                cameraId,
                damageId,
                description: 'Camera photo markers metadata',
                imageMarkers: roundedMarkers
            };
        });

        if (!updated) {
            nextList.push({
                name: this.CAMERA_MARKER_META_NAME,
                description: 'Camera photo markers metadata',
                cameraId,
                damageId,
                markerMetadata: true,
                imageMarkers: roundedMarkers
            });
        }

        return nextList;
    };

    private flushCurrentCameraPhotoMarkers = async () => {
        if (!this.state.cameraPhotoId) {
            return;
        }

        const markersToSave = this.state.cameraPhotoDraftMarker
            ? [...this.state.cameraPhotoMarkers, this.state.cameraPhotoDraftMarker]
            : this.state.cameraPhotoMarkers;

        await this.persistCameraPhotoMarkers(
            this.state.cameraPhotoId,
            this.state.cameraDamageSelectedId,
            markersToSave
        );
    };

    private normalizeCameraMarkerCollection = (payload: any): CameraPhotoMarker[] => {
        const raw = Array.isArray(payload)
            ? payload
            : (Array.isArray(payload?.markers) ? payload.markers : (Array.isArray(payload?.items) ? payload.items : []));

        return raw
            .map((entry: any, index: number) => {
                const width = Number(entry.width ?? entry.w ?? 0);
                const height = Number(entry.height ?? entry.h ?? 0);
                const centerX = Number(entry.centerX ?? entry.x ?? 0);
                const centerY = Number(entry.centerY ?? entry.y ?? 0);

                return {
                    id: String(entry.id ?? `mk-${index + 1}`),
                    centerX,
                    centerY,
                    width,
                    height
                };
            })
            .filter((entry: CameraPhotoMarker) => {
                return Number.isFinite(entry.centerX)
                    && Number.isFinite(entry.centerY)
                    && Number.isFinite(entry.width)
                    && Number.isFinite(entry.height)
                    && entry.width > 0
                    && entry.height > 0;
            });
    };

    private loadCameraMarkersFromLocalStorage = (cameraId: string, damageId: string): CameraPhotoMarker[] => {
        try {
            const raw = localStorage.getItem(this.getCameraMarkerStorageKey(cameraId, damageId));
            if (!raw) {
                return [];
            }
            return this.normalizeCameraMarkerCollection(JSON.parse(raw));
        } catch {
            return [];
        }
    };

    private saveCameraMarkersToLocalStorage = (cameraId: string, damageId: string, markers: CameraPhotoMarker[]) => {
        try {
            localStorage.setItem(this.getCameraMarkerStorageKey(cameraId, damageId), JSON.stringify({ markers }));
        } catch {
            // Ignore quota/localStorage errors.
        }
    };

    private loadAllCameraPhotoMarkers = async (cameraId: string) => {
        if (!cameraId) {
            return;
        }

        const loadSeq = ++this.markerLoadSeq;
        this.setState({ cameraPhotoInspectorLoading: true });

        const allMarkers: CameraPhotoMarker[] = [];
        const markerToDamageMap = new Map<string, string>();

        const damageList = this.state.cameraDamageList;

        if (!damageList.length) {
            if (loadSeq !== this.markerLoadSeq || this.state.cameraPhotoId !== cameraId) {
                return;
            }
            this.setState({
                cameraPhotoInspectorLoading: false,
                cameraPhotoMarkers: [],
                cameraPhotoDraftMarker: null,
                cameraPhotoSelectedMarkerId: '',
                cameraPhotoEditMode: false,
                markerToDamageMap: new Map()
            });
            return;
        }

        // Try to load from API first
        let apiDamageList: any[] | null = null;
        const damagesEndpoint = this.getDamagesEndpoint();
        if (damagesEndpoint) {
            try {
                const response = await fetch(damagesEndpoint, {
                    method: 'GET',
                    headers: buildAuthHeaders()
                });

                if (response.ok) {
                    const data = await response.json();
                    apiDamageList = Array.isArray(data?.damageList) ? data.damageList : null;
                } else if (response.status !== 404) {
                    throw new Error(`HTTP ${response.status}`);
                }
            } catch (error) {
                console.warn('[CameraPhotoMarkers] Unable to load from damages API, using local storage fallback.', error);
            }
        }

        if (loadSeq !== this.markerLoadSeq || this.state.cameraPhotoId !== cameraId) {
            return;
        }

        const seenMarkerIds = new Set<string>();
        const damageIdSet = new Set(damageList.map((d) => d.id));

        for (const damage of damageList) {
            if (!damageIdSet.has(damage.id)) {
                continue;
            }

            let markers: CameraPhotoMarker[] | null = null;

            if (apiDamageList !== null) {
                markers = this.extractCameraMarkersFromDamageList(apiDamageList, cameraId, damage.id);
            }

            const resolvedMarkers = markers ?? this.loadCameraMarkersFromLocalStorage(cameraId, damage.id);

            if (markers !== null) {
                this.saveCameraMarkersToLocalStorage(cameraId, damage.id, resolvedMarkers);
            }

            for (const marker of resolvedMarkers) {
                if (!seenMarkerIds.has(marker.id)) {
                    seenMarkerIds.add(marker.id);
                    allMarkers.push(marker);
                    markerToDamageMap.set(marker.id, damage.id);
                }
            }
        }

        if (loadSeq !== this.markerLoadSeq || this.state.cameraPhotoId !== cameraId) {
            return;
        }

        this.setState({
            cameraPhotoInspectorLoading: false,
            cameraPhotoMarkers: allMarkers,
            cameraPhotoDraftMarker: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            markerToDamageMap
        });
    };

    private loadCameraPhotoMarkers = async (cameraId: string, damageId = '') => {
        if (!cameraId) {
            return;
        }

        await this.loadAllCameraPhotoMarkers(cameraId);
    };

    private handleMarkerClickNavigation = (markerId: string) => {
        const damageId = this.state.markerToDamageMap.get(markerId);

        if (!damageId) {
            // Req 4.4: select marker visually without opening panel or navigating
            this.setState({ cameraPhotoSelectedMarkerId: markerId });
            return;
        }

        const damageIndex = this.state.cameraDamageList.findIndex(d => d.id === damageId);

        if (damageIndex < 0) {
            // Damage not found in list, just select visually
            this.setState({ cameraPhotoSelectedMarkerId: markerId });
            return;
        }

        // Req 4.2: open damage menu and navigate carousel to the damage index
        // setDamageCarouselIndex updates carousel state and sets cameraPhotoSelectedMarkerId
        // to the first marker of the damage. We override it afterwards with the clicked markerId.
        this.setDamageCarouselIndex(damageIndex);

        // Req 4.3: ensure the clicked marker is highlighted (overrides setDamageCarouselIndex default)
        // and open the damage menu
        this.setState({
            damageMenuVisible: true,
            cameraPhotoSelectedMarkerId: markerId
        });
    };

    private persistCameraPhotoMarkers = async (cameraId: string, damageId: string, markers: CameraPhotoMarker[]) => {
        if (!cameraId || !damageId) {
            return;
        }

        this.saveCameraMarkersToLocalStorage(cameraId, damageId, markers);

        const requestId = this.getRequestId();
        if (!requestId) {
            return;
        }

        const damagesEndpoint = this.getDamagesEndpoint();
        if (!damagesEndpoint) {
            return;
        }

        try {
            const currentResponse = await fetch(damagesEndpoint, {
                method: 'GET',
                headers: buildAuthHeaders()
            });

            if (currentResponse.status === 404) {
                console.warn('[CameraPhotoMarkers] Damages document not found (404). Skipping API marker save to avoid forced POST.');
                return;
            }

            if (!currentResponse.ok) {
                throw new Error(`GET damages failed: ${currentResponse.status}`);
            }

            const currentPayload = await currentResponse.json();
            const existingDamageList = Array.isArray(currentPayload?.damageList) ? currentPayload.damageList : [];
            const updatedDamageList = this.upsertCameraMarkerMetadataInDamageList(existingDamageList, cameraId, damageId, markers);

            const updateResponse = await fetch(damagesEndpoint, {
                method: 'PUT',
                headers: buildAuthHeaders(),
                body: JSON.stringify({
                    damageList: updatedDamageList
                })
            });

            if (!updateResponse.ok) {
                throw new Error(`PUT damages failed: ${updateResponse.status}`);
            }
        } catch (error) {
            console.warn('[CameraPhotoMarkers] Marker collection saved locally, damages API sync failed.', error);
        }
    };

    openCameraPhotoInspector = () => {
        if (!this.state.cameraPhotoId) {
            return;
        }

        this.setState({
            cameraPhotoInspectorOpen: true,
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoDraftMarker: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoCreateMode: false,
            cameraPhotoEditMode: false,
            cameraPhotoNaturalWidth: 0,
            cameraPhotoNaturalHeight: 0
        }, () => {
            this.loadCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId);
        });
    };

    openCameraPhotoInspectorInCreateMode = () => {
        if (!this.state.cameraPhotoId) {
            return;
        }

        this.setState({
            cameraPhotoInspectorOpen: true,
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoDraftMarker: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoCreateMode: true,
            cameraPhotoEditMode: false,
            cameraPhotoNaturalWidth: 0,
            cameraPhotoNaturalHeight: 0
        }, () => {
            this.loadCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId);
        });
    };

    closeCameraPhotoInspector = () => {
        this.markerDragContext = null;
        this.markerCollectionDirty = false;
        this.setState({
            cameraPhotoInspectorOpen: false,
            cameraPhotoInspectorLoading: false,
            cameraPhotoZoom: 1,
            cameraPhotoPan: { x: 0, y: 0 },
            cameraPhotoDraftMarker: null,
            cameraPhotoCreateMode: false,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false
        });
    };

    onInspectorImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const image = event.currentTarget;
        this.setState({
            cameraPhotoNaturalWidth: image.naturalWidth,
            cameraPhotoNaturalHeight: image.naturalHeight
        });
    };

    onNormalImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const image = event.currentTarget;
        this.setState({
            cameraPhotoNaturalWidth: image.naturalWidth,
            cameraPhotoNaturalHeight: image.naturalHeight
        });
    };

    onNormalImageError = () => {
        this.setState({ cameraPhotoLoadError: true });
    };

    onInspectorImageError = () => {
        this.setState({ cameraPhotoLoadError: true });
    };

    private getNormalPopupRectForMarker = (marker: CameraPhotoMarker) => {
        const frame = this.normalPhotoFrameRef.current;
        const image = this.normalPhotoImageRef.current;

        if (!frame || !image || !image.naturalWidth || !image.naturalHeight || !image.clientWidth || !image.clientHeight) {
            return null;
        }

        const left = image.offsetLeft + ((marker.centerX - (marker.width * 0.5)) / image.naturalWidth) * image.clientWidth;
        const top = image.offsetTop + ((marker.centerY - (marker.height * 0.5)) / image.naturalHeight) * image.clientHeight;
        const width = (marker.width / image.naturalWidth) * image.clientWidth;
        const height = (marker.height / image.naturalHeight) * image.clientHeight;

        return { left, top, width, height };
    };

    private getInspectorMetrics = () => {
        const viewport = this.inspectorViewportRef.current;
        const naturalWidth = this.state.cameraPhotoNaturalWidth;
        const naturalHeight = this.state.cameraPhotoNaturalHeight;

        if (!viewport || !naturalWidth || !naturalHeight) {
            return null;
        }

        const viewWidth = viewport.clientWidth;
        const viewHeight = viewport.clientHeight;
        if (!viewWidth || !viewHeight) {
            return null;
        }

        const fitScale = Math.min(viewWidth / naturalWidth, viewHeight / naturalHeight);
        const baseWidth = naturalWidth * fitScale;
        const baseHeight = naturalHeight * fitScale;
        const displayWidth = baseWidth * this.state.cameraPhotoZoom;
        const displayHeight = baseHeight * this.state.cameraPhotoZoom;
        const centerX = (viewWidth * 0.5) + this.state.cameraPhotoPan.x;
        const centerY = (viewHeight * 0.5) + this.state.cameraPhotoPan.y;

        return {
            viewport,
            naturalWidth,
            naturalHeight,
            viewWidth,
            viewHeight,
            baseWidth,
            baseHeight,
            displayWidth,
            displayHeight,
            centerX,
            centerY
        };
    };

    private clampPan = (pan: { x: number; y: number }, zoom = this.state.cameraPhotoZoom) => {
        const viewport = this.inspectorViewportRef.current;
        const naturalWidth = this.state.cameraPhotoNaturalWidth;
        const naturalHeight = this.state.cameraPhotoNaturalHeight;

        if (!viewport || !naturalWidth || !naturalHeight) {
            return pan;
        }

        return clampPanUtil(pan, zoom, viewport.clientWidth, viewport.clientHeight, naturalWidth, naturalHeight);
    };

    private attachWheelListener = (): void => {
        const viewport = this.inspectorViewportRef.current;
        if (viewport) {
            viewport.addEventListener('wheel', this.handleInspectorWheel, { passive: false });
        }
    };

    private detachWheelListener = (): void => {
        const viewport = this.inspectorViewportRef.current;
        if (viewport) {
            viewport.removeEventListener('wheel', this.handleInspectorWheel);
        }
    };

    private handleInspectorWheel = (event: WheelEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        if (!this.state.cameraPhotoInspectorOpen) {
            return;
        }

        const delta = normalizeWheelDelta(event.deltaY, event.deltaMode);
        const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, this.state.cameraPhotoZoom + delta));
        const newPan = newZoom <= 1
            ? { x: 0, y: 0 }
            : this.clampPan(this.state.cameraPhotoPan, newZoom);

        this.setState({ cameraPhotoZoom: newZoom, cameraPhotoPan: newPan });
    };

    onInspectorZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const zoom = Number(event.target.value);
        this.setState((prev) => ({
            cameraPhotoZoom: zoom,
            cameraPhotoPan: this.clampPan(prev.cameraPhotoPan, zoom)
        }));
    };

    private clientToImagePixel = (clientX: number, clientY: number) => {
        const metrics = this.getInspectorMetrics();
        if (!metrics) {
            return null;
        }

        const rect = metrics.viewport.getBoundingClientRect();
        const xInViewport = clientX - rect.left;
        const yInViewport = clientY - rect.top;

        const left = metrics.centerX - (metrics.displayWidth * 0.5);
        const top = metrics.centerY - (metrics.displayHeight * 0.5);

        if (
            xInViewport < left ||
            yInViewport < top ||
            xInViewport > left + metrics.displayWidth ||
            yInViewport > top + metrics.displayHeight
        ) {
            return null;
        }

        return {
            x: ((xInViewport - left) / metrics.displayWidth) * metrics.naturalWidth,
            y: ((yInViewport - top) / metrics.displayHeight) * metrics.naturalHeight
        };
    };

    private getDisplayRectForMarker = (marker: CameraPhotoMarker) => {
        const metrics = this.getInspectorMetrics();
        if (!metrics) {
            return null;
        }

        const left = metrics.centerX - (metrics.displayWidth * 0.5)
            + ((marker.centerX - (marker.width * 0.5)) / metrics.naturalWidth) * metrics.displayWidth;
        const top = metrics.centerY - (metrics.displayHeight * 0.5)
            + ((marker.centerY - (marker.height * 0.5)) / metrics.naturalHeight) * metrics.displayHeight;
        const width = (marker.width / metrics.naturalWidth) * metrics.displayWidth;
        const height = (marker.height / metrics.naturalHeight) * metrics.displayHeight;

        return { left, top, width, height };
    };

    toggleCreateCameraPhotoMarker = () => {
        this.setState({
            cameraPhotoCreateMode: !this.state.cameraPhotoCreateMode,
            cameraPhotoDraftMarker: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false
        });
    };

    private updateMarker = (markerId: string, markerIsDraft: boolean, updater: (entry: CameraPhotoMarker) => CameraPhotoMarker) => {
        if (markerIsDraft) {
            this.setState((prev: AppState): { cameraPhotoDraftMarker: CameraPhotoMarker } | null => {
                if (!prev.cameraPhotoDraftMarker || prev.cameraPhotoDraftMarker.id !== markerId) {
                    return null;
                }
                return { cameraPhotoDraftMarker: updater(prev.cameraPhotoDraftMarker) };
            });
            return;
        }

        this.setState((prev) => ({
            cameraPhotoMarkers: prev.cameraPhotoMarkers.map((entry) => {
                return entry.id === markerId ? updater(entry) : entry;
            })
        }));
    };

    onInspectorViewportClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (this.markerDragContext) {
            return;
        }

        if (!this.state.cameraPhotoCreateMode) {
            this.setState({
                cameraPhotoSelectedMarkerId: '',
                cameraPhotoEditMode: false
            });
            return;
        }

        const imagePixel = this.clientToImagePixel(event.clientX, event.clientY);
        if (!imagePixel) {
            return;
        }

        const defaultWidth = Math.max(40, Math.round(this.state.cameraPhotoNaturalWidth * 0.12));
        const defaultHeight = Math.max(40, Math.round(this.state.cameraPhotoNaturalHeight * 0.12));
        const draftId = `draft-${Date.now()}`;

        this.setState({
            cameraPhotoDraftMarker: {
                id: draftId,
                centerX: imagePixel.x,
                centerY: imagePixel.y,
                width: defaultWidth,
                height: defaultHeight
            },
            cameraPhotoSelectedMarkerId: draftId,
            cameraPhotoEditMode: true,
            cameraPhotoCreateMode: false
        });
    };

    confirmCreateCameraPhotoMarker = async () => {
        const draft = this.state.cameraPhotoDraftMarker;
        if (!draft || !this.state.cameraPhotoId) {
            return;
        }

        // Check global damages (not just camera-local) to determine association options
        const globalDamages = window.viewer?.cameraDamageRecords || [];
        const hasGlobalDamages = globalDamages.length > 0;

        if (!hasGlobalDamages) {
            // No global damages exist: show "Create New Damage" + "Cancel"
            this.setState({
                markerDamagePromptOpen: true,
                markerDamagePromptType: 'noDamage',
                cameraPhotoCreateMode: false,
                pendingMarkerAfterDamage: draft
            });
            return;
        }

        // Global damages exist: show "Create New Damage" + "Associate To Damage" + "Cancel"
        this.setState({
            markerDamagePromptOpen: true,
            markerDamagePromptType: 'selectDamage',
            cameraPhotoCreateMode: false,
            pendingMarkerAfterDamage: draft
        });
    };

    cancelCreateCameraPhotoMarker = () => {
        this.setState({
            cameraPhotoDraftMarker: null,
            pendingMarkerAfterDamage: null,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false,
            cameraPhotoCreateMode: false
        });
    };

    selectCameraPhotoMarker = (markerId: string, event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        this.setState({
            cameraPhotoSelectedMarkerId: markerId,
            cameraPhotoEditMode: true,
            cameraPhotoCreateMode: false
        });
    };

    startMarkerMove = (markerId: string, markerIsDraft: boolean, event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        const marker = markerIsDraft
            ? this.state.cameraPhotoDraftMarker
            : this.state.cameraPhotoMarkers.find((entry) => entry.id === markerId) || null;

        if (!marker) {
            return;
        }

        this.markerDragContext = {
            mode: 'move',
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPan: this.state.cameraPhotoPan,
            markerId,
            markerIsDraft,
            initialMarker: { ...marker }
        };
    };

    startMarkerResize = (markerId: string, markerIsDraft: boolean, event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        const marker = markerIsDraft
            ? this.state.cameraPhotoDraftMarker
            : this.state.cameraPhotoMarkers.find((entry) => entry.id === markerId) || null;

        if (!marker) {
            return;
        }

        this.markerDragContext = {
            mode: 'resize',
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPan: this.state.cameraPhotoPan,
            markerId,
            markerIsDraft,
            initialMarker: { ...marker }
        };
    };

    startInspectorPan = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        if (this.state.cameraPhotoZoom <= 1) {
            return;
        }

        this.markerDragContext = {
            mode: 'pan',
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPan: this.state.cameraPhotoPan,
            markerId: '',
            markerIsDraft: false,
            initialMarker: null
        };
    };

    private handleInspectorMouseMove = (event: MouseEvent) => {
        if (!this.markerDragContext) {
            return;
        }

        const metrics = this.getInspectorMetrics();
        if (!metrics) {
            return;
        }

        const deltaX = event.clientX - this.markerDragContext.startClientX;
        const deltaY = event.clientY - this.markerDragContext.startClientY;

        if (this.markerDragContext.mode === 'pan') {
            const nextPan = {
                x: this.markerDragContext.startPan.x + deltaX,
                y: this.markerDragContext.startPan.y + deltaY
            };
            this.setState({ cameraPhotoPan: this.clampPan(nextPan) });
            return;
        }

        if (!this.markerDragContext.initialMarker) {
            return;
        }

        const originalDeltaX = (deltaX / metrics.displayWidth) * metrics.naturalWidth;
        const originalDeltaY = (deltaY / metrics.displayHeight) * metrics.naturalHeight;
        const sourceMarker = this.markerDragContext.initialMarker;

        if (this.markerDragContext.mode === 'move') {
            const halfWidth = sourceMarker.width * 0.5;
            const halfHeight = sourceMarker.height * 0.5;

            this.updateMarker(this.markerDragContext.markerId, this.markerDragContext.markerIsDraft, (entry) => ({
                ...entry,
                centerX: Math.min(metrics.naturalWidth - halfWidth, Math.max(halfWidth, sourceMarker.centerX + originalDeltaX)),
                centerY: Math.min(metrics.naturalHeight - halfHeight, Math.max(halfHeight, sourceMarker.centerY + originalDeltaY))
            }));
        }

        if (this.markerDragContext.mode === 'resize') {
            this.updateMarker(this.markerDragContext.markerId, this.markerDragContext.markerIsDraft, (entry) => ({
                ...entry,
                width: Math.max(24, Math.min(metrics.naturalWidth, sourceMarker.width + (originalDeltaX * 2))),
                height: Math.max(24, Math.min(metrics.naturalHeight, sourceMarker.height + (originalDeltaY * 2)))
            }));
        }

        if (!this.markerDragContext.markerIsDraft) {
            this.markerCollectionDirty = true;
        }
    };

    private handleInspectorMouseUp = async () => {
        const hadDirtyMarkers = this.markerCollectionDirty;
        this.markerDragContext = null;
        this.markerCollectionDirty = false;

        if (hadDirtyMarkers && this.state.cameraPhotoId) {
            await this.persistCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId, this.state.cameraPhotoMarkers);
        }
    };

    toggleCameraPhotoMarkerEditMode = () => {
        if (!this.state.cameraPhotoSelectedMarkerId) {
            return;
        }

        this.setState((prev) => ({
            cameraPhotoEditMode: !prev.cameraPhotoEditMode
        }));
    };

    deleteSelectedCameraPhotoMarker = async () => {
        if (!this.state.cameraPhotoSelectedMarkerId || !this.state.cameraPhotoId) {
            return;
        }

        if (this.state.cameraPhotoDraftMarker && this.state.cameraPhotoDraftMarker.id === this.state.cameraPhotoSelectedMarkerId) {
            this.setState({
                cameraPhotoDraftMarker: null,
                cameraPhotoSelectedMarkerId: '',
                cameraPhotoEditMode: false
            });
            return;
        }

        const nextMarkers = this.state.cameraPhotoMarkers.filter((entry) => entry.id !== this.state.cameraPhotoSelectedMarkerId);
        this.setState({
            cameraPhotoMarkers: nextMarkers,
            cameraPhotoSelectedMarkerId: '',
            cameraPhotoEditMode: false
        });
        await this.persistCameraPhotoMarkers(this.state.cameraPhotoId, this.state.cameraDamageSelectedId, nextMarkers);
    };

    render() {
        const requestId = this.getRequestId();
        const totalPhotos = this.state.cameraPhotoCarousel.length;
        const hasSceneLoaded = Boolean(this.state.scene?.urls?.length || this.state.scene?.filenames?.length);
        const photoLabel = totalPhotos > 0
            ? `${String(this.state.cameraPhotoIndex + 1).padStart(2, '0')}/${String(totalPhotos).padStart(2, '0')}`
            : '';
        const allInspectorMarkers = this.state.cameraPhotoDraftMarker
            ? [...this.state.cameraPhotoMarkers, this.state.cameraPhotoDraftMarker]
            : this.state.cameraPhotoMarkers;
        const selectedMarker = allInspectorMarkers.find((entry) => entry.id === this.state.cameraPhotoSelectedMarkerId) || null;
        const selectedMarkerRect = selectedMarker ? this.getDisplayRectForMarker(selectedMarker) : null;
        const damageCount = this.state.cameraDamageList.length;
        const damageCarouselSlots = Math.max(1, damageCount);
        const damageCarouselLabel = `${Math.min(this.state.cameraDamageCarouselIndex + 1, damageCarouselSlots)}/${damageCarouselSlots}`;
        const shouldFloatDamagePanel = this.state.cameraDamageCenterModalOpen && this.state.cameraDamageMode !== 'view';
        const apiConfig = this.getApiConfig();

        return <div id="application-container">
            <Container id="panel-left" flex resizable='right' resizeMin={220} resizeMax={800}>
                <div className="header" style={{ display: 'none' }}>
                    <div id="title">
                        <img src={'static/Logo_VYSIA_White_Fluor.png'}/>
                        <div>{`${t('app.title')} v${appVersion}`}</div>
                    </div>
                </div>
                <div id="panel-toggle">
                    <img src={'static/Logo_VYSIA_White_Fluor.png'} style={{height: '35px', width: 'auto'}}/>
                </div>
                <LeftPanel observerData={this.state} setProperty={this._setStateProperty} />
            </Container>
            <div id='canvas-wrapper'>
                <canvas id="application-canvas" ref={this.canvasRef} />
                
                {/* Gallery Button - Top Center (temporarily disabled for carousel migration)
                {requestId && !this.state.ui?.spinner && (
                    <button
                        className="gallery-open-button"
                        onClick={this.toggleGallery}
                        title={t('gallery.openGallery')}
                    >
                        {t('gallery.openGallery')}
                    </button>
                )}
                */}

                {/* RightPanel consolidates: GearButton (Language+Quality), OrbitalToggle, CameraSliders, PositionsMenu, NavAugmentada, DamageToggle */}
                <RightPanel
                    observerData={this.state}
                    setProperty={this._setStateProperty}
                    requestId={requestId}
                    hasSceneLoaded={hasSceneLoaded}
                    cameraPositionsVisible={this.state.cameraPositionsVisible}
                    onToggleCameraPositions={this.toggleCameraPositions}
                />

                <LoadControls setProperty={this._setStateProperty}/>
                <SelectedNode sceneData={this.state.scene} />
                <PopupPanel observerData={this.state} setProperty={this._setStateProperty} />
                <ErrorBox observerData={this.state} />
                <LoadingBar 
                    progress={this.state.ui?.loadingProgress || 0}
                    isVisible={this.state.ui?.spinner || false}
                    message={this.state.ui?.loadingMessage || t('app.loading')}
                />
                <Spinner id="spinner" size={30} hidden={true} />
                
                <FallbackGallery
                    isOpen={this.state.fallbackGalleryOpen}
                    photos={this.state.fallbackGalleryPhotos}
                    onClose={() => this.setState({ fallbackGalleryOpen: false })}
                    requestId={requestId}
                    apiBaseUrl={apiConfig.apiBaseUrl}
                    apiStagePath={apiConfig.apiStage}
                    apiPrefix={apiConfig.apiPrefix}
                />

                {/* Image Gallery (temporarily disabled for carousel migration)
                {requestId && (
                    <ImageGallery
                        isOpen={this.state.galleryOpen}
                        onClose={this.toggleGallery}
                        requestId={requestId}
                        apiBaseUrl={apiConfig.apiBaseUrl}
                        apiStage={apiConfig.apiStage}
                        apiPrefix={apiConfig.apiPrefix}
                        damages={[]}
                    />
                )}
                */}

                {/* Camera Photo Popup */}
                {this.state.cameraPhotoOpen && (
                    <div
                        className="gallery-enlarged-overlay"
                        onWheel={e => e.stopPropagation()}
                    >
                        <div
                            className="gallery-enlarged-container"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="gallery-enlarged-media">
                                <div className="gallery-enlarged-image-frame" ref={this.normalPhotoFrameRef}>
                                    <button
                                        className="gallery-enlarged-action gallery-enlarged-download"
                                        onClick={this.downloadCurrentCameraPhoto}
                                        title={t('gallery.download')}
                                    >
                                        ↓
                                    </button>
                                    <button
                                        className="gallery-enlarged-action gallery-enlarged-close"
                                        onClick={this.closeCameraPhotoPopup}
                                        title={t('gallery.close')}
                                    >
                                        ×
                                    </button>
                                    <button
                                        className="gallery-enlarged-action gallery-enlarged-expand"
                                        onClick={this.openCameraPhotoInspectorInCreateMode}
                                        title={t('damage.createWithMark')}
                                    >
                                        +
                                    </button>
                                    {totalPhotos > 0 && (
                                        <button
                                            className="gallery-enlarged-nav gallery-enlarged-prev"
                                            onClick={this.goToPreviousCameraPhoto}
                                            title={t('gallery.previous')}
                                        >
                                            ‹
                                        </button>
                                    )}

                                    {this.state.cameraPhotoLoadError ? (
                                        <div className="camera-photo-error-placeholder">
                                            <span className="camera-photo-error-icon">⚠</span>
                                            <span>{t('gallery.imageUnavailable')}</span>
                                        </div>
                                    ) : (
                                        <img
                                            ref={this.normalPhotoImageRef}
                                            src={this.state.cameraPhotoUrl}
                                            alt={this.state.cameraPhotoId}
                                            className="gallery-enlarged-image"
                                            onLoad={this.onNormalImageLoad}
                                            onError={this.onNormalImageError}
                                        />
                                    )}
                                    {this.state.cameraPhotoMarkers.length > 0 && (
                                        <div className="camera-photo-legend">
                                            <div className="camera-photo-legend-item">
                                                <span className="camera-photo-legend-swatch camera-photo-legend-swatch--selected" />
                                                <span>{t('damage.legendSelected')}</span>
                                            </div>
                                            <div className="camera-photo-legend-item">
                                                <span className="camera-photo-legend-swatch camera-photo-legend-swatch--unselected" />
                                                <span>{t('damage.legendUnselected')}</span>
                                            </div>
                                        </div>
                                    )}
                                    {this.state.cameraPhotoMarkers.map((marker) => {
                                        const markerRect = this.getNormalPopupRectForMarker(marker);
                                        if (!markerRect) {
                                            return null;
                                        }

                                        const isHighlighted = this.state.cameraDamageSelectedId
                                            && this.state.markerToDamageMap.get(marker.id) === this.state.cameraDamageSelectedId;
                                        const isSelected = this.state.cameraPhotoSelectedMarkerId === marker.id;
                                        const markerClass = [
                                            'camera-photo-marker',
                                            'camera-photo-marker--readonly',
                                            isHighlighted ? 'camera-photo-marker--highlighted' : '',
                                            isSelected ? 'camera-photo-marker--selected' : ''
                                        ].filter(Boolean).join(' ');

                                        return (
                                            <div
                                                key={`normal-${marker.id}`}
                                                className={markerClass}
                                                style={{
                                                    left: `${markerRect.left}px`,
                                                    top: `${markerRect.top}px`,
                                                    width: `${markerRect.width}px`,
                                                    height: `${markerRect.height}px`
                                                }}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    this.handleMarkerClickNavigation(marker.id);
                                                }}
                                            />
                                        );
                                    })}
                                    {totalPhotos > 0 && (
                                        <button
                                            className="gallery-enlarged-nav gallery-enlarged-next"
                                            onClick={this.goToNextCameraPhoto}
                                            title={t('gallery.next')}
                                        >
                                            ›
                                        </button>
                                    )}

                                </div>
                            </div>
                            <div className="gallery-enlarged-info-row">
                                <div className="gallery-enlarged-info">
                                    {photoLabel ? `${this.state.cameraPhotoId} (${photoLabel})` : this.state.cameraPhotoId}
                                </div>
                                <button
                                    className={`damage-menu-toggle${damageCount > 0 ? '' : ' damage-menu-toggle--disabled'}`}
                                    disabled={damageCount === 0}
                                    onClick={() => { if (damageCount > 0) this.setState({ damageMenuVisible: !this.state.damageMenuVisible }); }}
                                    title={t('damage.toggleMenu')}
                                >
                                    {t('damage.toggleMenu')}
                                </button>
                            </div>

                            {shouldFloatDamagePanel && <div className="camera-damage-center-overlay" onClick={this.closeDamageCenterModal} />}

                            {(this.state.damageMenuVisible || shouldFloatDamagePanel) && (
                            <div className={`camera-damage-panel${shouldFloatDamagePanel ? ' camera-damage-panel--floating' : ''}`}>
                                <div className="camera-damage-panel-title">{t('damage.sheetTitle')}</div>
                                {this.state.cameraDamageMode === 'view' && (
                                    <>
                                        {damageCount > 0 && this.state.cameraDamageCurrent ? (
                                            <>
                                                <div className="camera-damage-row"><strong>{t('markers.tableName')}:</strong> {this.state.cameraDamageCurrent.name}</div>
                                                <div className="camera-damage-row"><strong>{t('markers.tableDescription')}:</strong> {this.state.cameraDamageCurrent.description}</div>
                                                <div className="camera-damage-row"><strong>{t('markers.tableSide')}:</strong> {this.state.cameraDamageCurrent.selectValue}</div>
                                                <div className="camera-damage-actions">
                                                    <button className="camera-damage-button" onClick={() => this.setState({ cameraDamageMode: 'new' })}>{t('markers.edit')}</button>
                                                    <button className="camera-damage-button" onClick={this.openCameraDamageNotes}>{t('cameraPositions.viewNotes')}</button>
                                                    <button className="camera-damage-button" onClick={this.openDeleteCameraDamageConfirm}>{t('markers.delete')}</button>
                                                </div>
                                            </>
                                        ) : null}
                                        <div className="camera-damage-actions">
                                            <button className="camera-damage-button" onClick={this.closeCameraPhotoPopup}>{t('gallery.close')}</button>
                                        </div>
                                        <div className="camera-damage-carousel-row">
                                            <button
                                                className="camera-damage-button camera-damage-button--mini"
                                                onClick={() => this.setDamageCarouselIndex(this.state.cameraDamageCarouselIndex - 1)}
                                            >
                                                {t('gallery.previous')}
                                            </button>
                                            <div className="camera-damage-pagination">{damageCarouselLabel}</div>
                                            <button
                                                className="camera-damage-button camera-damage-button--mini"
                                                onClick={() => this.setDamageCarouselIndex(this.state.cameraDamageCarouselIndex + 1)}
                                            >
                                                {t('gallery.next')}
                                            </button>
                                        </div>
                                    </>
                                )}

                                {this.state.cameraDamageMode === 'select' && (
                                    <div className="camera-damage-form">
                                        <button className="camera-damage-button" onClick={() => this.setState({ cameraDamageMode: 'existing' })}>{t('cameraPositions.useExistingDamage')}</button>
                                        <button className="camera-damage-button" onClick={() => this.setState({ cameraDamageMode: 'new' })}>{t('cameraPositions.createNewDamage')}</button>
                                        <button
                                            className="camera-damage-button"
                                            onClick={() => this.setState({ cameraDamageMode: 'view', cameraDamageCenterModalOpen: false })}
                                        >
                                            {t('markers.cancel')}
                                        </button>
                                    </div>
                                )}

                                {this.state.cameraDamageMode === 'existing' && (
                                    <div className="camera-damage-form">
                                        <select
                                            className="camera-damage-select"
                                            value={this.state.selectedExistingDamageId}
                                            onChange={(e) => this.setState({ selectedExistingDamageId: e.target.value })}
                                        >
                                            <option value="">{t('markers.selectDamage')}</option>
                                            {this.state.cameraDamageOptions.map(option => (
                                                <option key={option.id} value={option.id}>
                                                    {`${option.name} - ${option.selectValue}`}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="camera-damage-actions">
                                            <button className="camera-damage-button" onClick={this.assignExistingDamage}>{t('markers.accept')}</button>
                                            <button className="camera-damage-button" onClick={() => this.setState({ cameraDamageMode: 'select' })}>{t('markers.cancel')}</button>
                                        </div>
                                    </div>
                                )}

                                {this.state.cameraDamageMode === 'new' && (
                                    <div className="camera-damage-form">
                                        <input
                                            className="camera-damage-input"
                                            type="text"
                                            value={this.state.cameraDamageForm.name}
                                            placeholder={t('markers.sphereName')}
                                            onChange={(e) => this.handleDamageFieldChange('name', e.target.value)}
                                        />
                                        <textarea
                                            className="camera-damage-textarea"
                                            value={this.state.cameraDamageForm.description}
                                            placeholder={t('markers.sphereDescription')}
                                            onChange={(e) => this.handleDamageFieldChange('description', e.target.value)}
                                        />
                                        <select
                                            className="camera-damage-select"
                                            value={this.state.cameraDamageForm.selectValue}
                                            onChange={(e) => this.handleDamageFieldChange('selectValue', e.target.value)}
                                        >
                                            <option value="">{t('markers.selectSide')}</option>
                                            <option value="Front">{t('markers.sideFront')}</option>
                                            <option value="Left">{t('markers.sideLeft')}</option>
                                            <option value="Right">{t('markers.sideRight')}</option>
                                            <option value="Back">{t('markers.sideBack')}</option>
                                        </select>
                                        <div className="camera-damage-actions">
                                            <button className="camera-damage-button" onClick={this.saveCameraDamage}>{t('markers.accept')}</button>
                                            <button
                                                className="camera-damage-button"
                                                onClick={() => this.setState({
                                                    cameraDamageMode: this.state.cameraDamageCurrent ? 'view' : 'select',
                                                    cameraDamageCenterModalOpen: false
                                                })}
                                            >
                                                {t('markers.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {this.state.cameraDamageMode === 'confirmDelete' && (
                                    <div className="camera-damage-form">
                                        <div className="camera-damage-row">{t('cameraPositions.deleteConfirmMessage')}</div>
                                        <div className="camera-damage-actions">
                                            <button className="camera-damage-button" onClick={this.confirmDeleteCameraDamage}>{t('markers.delete')}</button>
                                            <button
                                                className="camera-damage-button"
                                                onClick={() => this.setState({ cameraDamageMode: 'view', cameraDamageCenterModalOpen: false })}
                                            >
                                                {t('markers.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {this.state.cameraDamageMode === 'notes' && (
                                    <div className="camera-damage-form">
                                        {this.state.cameraDamageNoteDeleteConfirmOpen ? (
                                            <div className="camera-damage-form">
                                                <div className="camera-damage-row">{t('cameraPositions.deleteConfirmMessage')}</div>
                                                <div className="camera-damage-actions">
                                                    <button className="camera-damage-button" onClick={this.confirmDeleteCameraDamageNote}>{t('markers.delete')}</button>
                                                    <button className="camera-damage-button" onClick={this.cancelDeleteCameraDamageNoteConfirm}>{t('markers.cancel')}</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="camera-damage-notes-list">
                                                    {this.state.cameraDamageNotes.length === 0 ? (
                                                        <div className="camera-damage-row">{t('cameraPositions.noNotes')}</div>
                                                    ) : (
                                                        this.state.cameraDamageNotes.map((note) => {
                                                            const isSelected = note.id === this.state.cameraDamageSelectedNoteId;
                                                            return (
                                                                <button
                                                                    key={note.id}
                                                                    className={`camera-damage-note-item${isSelected ? ' camera-damage-note-item--selected' : ''}`}
                                                                    onClick={() => this.setState({ cameraDamageSelectedNoteId: note.id })}
                                                                    type="button"
                                                                >
                                                                    <div className="camera-damage-note-item__meta">
                                                                        <strong>{this.formatNoteDateTimeForList(note.dateTime)}</strong>
                                                                        <span>{note.author}</span>
                                                                    </div>
                                                                    <div className="camera-damage-note-item__message">{note.message}</div>
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>

                                                {this.state.cameraDamageNoteEditorMode !== 'none' && (
                                                    <div className="camera-damage-note-editor">
                                                        <input
                                                            className="camera-damage-input"
                                                            type="datetime-local"
                                                            value={this.state.cameraDamageNoteForm.dateTime}
                                                            onChange={(e) => this.handleCameraDamageNoteFieldChange('dateTime', e.target.value)}
                                                        />
                                                        <input
                                                            className="camera-damage-input"
                                                            type="text"
                                                            value={this.state.cameraDamageNoteForm.author}
                                                            placeholder={t('cameraPositions.noteAuthor')}
                                                            onChange={(e) => this.handleCameraDamageNoteFieldChange('author', e.target.value)}
                                                        />
                                                        <textarea
                                                            className="camera-damage-textarea"
                                                            value={this.state.cameraDamageNoteForm.message}
                                                            placeholder={t('cameraPositions.noteMessage')}
                                                            onChange={(e) => this.handleCameraDamageNoteFieldChange('message', e.target.value)}
                                                        />
                                                        <div className="camera-damage-actions">
                                                            <button className="camera-damage-button" onClick={this.saveCameraDamageNote}>{t('markers.accept')}</button>
                                                            <button className="camera-damage-button" onClick={this.cancelCameraDamageNoteEdition}>{t('markers.cancel')}</button>
                                                        </div>
                                                    </div>
                                                )}

                                                {this.state.cameraDamageNoteEditorMode === 'none' && (
                                                    <div className="camera-damage-actions">
                                                        <button className="camera-damage-button" onClick={this.startNewCameraDamageNote}>{t('cameraPositions.newNote')}</button>
                                                        <button
                                                            className="camera-damage-button"
                                                            onClick={this.startEditCameraDamageNote}
                                                            disabled={!this.state.cameraDamageSelectedNoteId}
                                                        >
                                                            {t('cameraPositions.editSelectedNote')}
                                                        </button>
                                                        <button
                                                            className="camera-damage-button"
                                                            onClick={this.openDeleteCameraDamageNoteConfirm}
                                                            disabled={!this.state.cameraDamageSelectedNoteId}
                                                        >
                                                            {t('cameraPositions.deleteSelectedNote')}
                                                        </button>
                                                        <button className="camera-damage-button" onClick={this.exitCameraDamageNotes}>{t('cameraPositions.exitNotes')}</button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    </div>
                )}

                {this.state.cameraPhotoInspectorOpen && (
                    <div className="camera-photo-inspector-overlay" onClick={this.closeCameraPhotoInspector}>
                        <div className="camera-photo-inspector-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="camera-photo-inspector-header">
                                <button
                                    className={`camera-photo-inspector-button${this.state.cameraPhotoCreateMode ? ' camera-photo-inspector-button--active' : ''}`}
                                    onClick={this.toggleCreateCameraPhotoMarker}
                                    title={t('markers.create')}
                                >
                                    <svg className="camera-photo-pin-icon" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                        <circle cx="12" cy="9" r="2.8" className="camera-photo-pin-icon__center" />
                                    </svg>
                                </button>
                                <button
                                    className="camera-photo-inspector-button"
                                    onClick={this.closeCameraPhotoInspector}
                                    title={t('gallery.close')}
                                >
                                    ×
                                </button>
                            </div>

                            <div
                                className="camera-photo-inspector-viewport"
                                ref={this.inspectorViewportRef}
                                onClick={this.onInspectorViewportClick}
                                onMouseDown={this.startInspectorPan}
                            >
                                {this.state.cameraPhotoInspectorLoading && (
                                    <div className="camera-photo-inspector-loading">{t('gallery.loading')}</div>
                                )}
                                {this.state.cameraPhotoLoadError ? (
                                    <div className="camera-photo-error-placeholder camera-photo-error-placeholder--inspector">
                                        <span className="camera-photo-error-icon">⚠</span>
                                        <span>{t('gallery.imageUnavailable')}</span>
                                    </div>
                                ) : (
                                    <img
                                        src={this.state.cameraPhotoUrl}
                                        alt={this.state.cameraPhotoId}
                                        className="camera-photo-inspector-image"
                                        onLoad={this.onInspectorImageLoad}
                                        onError={this.onInspectorImageError}
                                        draggable={false}
                                        style={{
                                            transform: `translate(-50%, -50%) translate(${this.state.cameraPhotoPan.x}px, ${this.state.cameraPhotoPan.y}px) scale(${this.state.cameraPhotoZoom})`
                                        }}
                                    />
                                )}

                                {allInspectorMarkers.map((marker) => {
                                    const markerRect = this.getDisplayRectForMarker(marker);
                                    if (!markerRect) {
                                        return null;
                                    }

                                    const isSelected = marker.id === this.state.cameraPhotoSelectedMarkerId;
                                    const isDraft = this.state.cameraPhotoDraftMarker?.id === marker.id;
                                    const isHighlighted = this.state.cameraDamageSelectedId
                                        && this.state.markerToDamageMap.get(marker.id) === this.state.cameraDamageSelectedId;
                                    const markerClassName = [
                                        'camera-photo-marker',
                                        isSelected ? 'camera-photo-marker--selected' : '',
                                        isHighlighted ? 'camera-photo-marker--highlighted' : '',
                                        isDraft ? 'camera-photo-marker--draft' : ''
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <div
                                            key={marker.id}
                                            className={markerClassName}
                                            style={{
                                                left: `${markerRect.left}px`,
                                                top: `${markerRect.top}px`,
                                                width: `${markerRect.width}px`,
                                                height: `${markerRect.height}px`
                                            }}
                                            onClick={(event) => this.selectCameraPhotoMarker(marker.id, event)}
                                            onMouseDown={(event) => {
                                                if (isSelected) {
                                                    this.startMarkerMove(marker.id, isDraft, event);
                                                }
                                            }}
                                        >
                                            {isSelected && (
                                                <div
                                                    className="camera-photo-marker-resize"
                                                    onMouseDown={(event) => this.startMarkerResize(marker.id, isDraft, event)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}

                                {this.state.cameraPhotoDraftMarker && (() => {
                                    const draftRect = this.getDisplayRectForMarker(this.state.cameraPhotoDraftMarker);
                                    if (!draftRect) {
                                        return null;
                                    }
                                    return (
                                        <div
                                            className="camera-photo-marker-contextual-buttons"
                                            style={{
                                                left: `${draftRect.left + draftRect.width}px`,
                                                top: `${draftRect.top + draftRect.height + 4}px`
                                            }}
                                        >
                                            <button
                                                className="camera-photo-marker-contextual-btn camera-photo-marker-contextual-btn--confirm"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    this.confirmCreateCameraPhotoMarker();
                                                }}
                                                title={t('damage.confirmMark')}
                                            >
                                                ✓
                                            </button>
                                            <button
                                                className="camera-photo-marker-contextual-btn camera-photo-marker-contextual-btn--cancel"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    this.cancelCreateCameraPhotoMarker();
                                                }}
                                                title={t('damage.cancelMark')}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })()}

                                {selectedMarkerRect && !this.state.cameraPhotoDraftMarker && (
                                    <div
                                        className="camera-photo-marker-actions"
                                        style={{
                                            left: `${Math.min(selectedMarkerRect.left + selectedMarkerRect.width - 92, this.inspectorViewportRef.current ? this.inspectorViewportRef.current.clientWidth - 96 : selectedMarkerRect.left)}px`,
                                            top: `${Math.max(8, selectedMarkerRect.top - 42)}px`
                                        }}
                                    >
                                        <button
                                            className={`camera-photo-inspector-inline-action${this.state.cameraPhotoEditMode ? ' camera-photo-inspector-inline-action--active' : ''}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                this.toggleCameraPhotoMarkerEditMode();
                                            }}
                                            title={t('markers.edit')}
                                        >
                                            ✎
                                        </button>
                                        <button
                                            className="camera-photo-inspector-inline-action"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                this.deleteSelectedCameraPhotoMarker();
                                            }}
                                            title={t('markers.delete')}
                                        >
                                            🗑
                                        </button>
                                    </div>
                                )}

                                <div className="camera-photo-inspector-zoom-controls">
                                    <span>{t('position.zoom')}</span>
                                    <input
                                        type="range"
                                        min={1}
                                        max={4}
                                        step={0.05}
                                        value={this.state.cameraPhotoZoom}
                                        onChange={this.onInspectorZoomChange}
                                    />
                                    <span>{`${this.state.cameraPhotoZoom.toFixed(2)}x`}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.markerDamagePromptOpen && (
                    <div className="camera-damage-center-overlay" onClick={this.closeMarkerPrompt}>
                        <div className="camera-damage-center-modal" onClick={(event) => event.stopPropagation()}>
                            <div className="camera-damage-row">
                                {this.state.markerDamagePromptType === 'noDamage'
                                    ? t('cameraPositions.markerGlobalNoDamagePrompt')
                                    : t('cameraPositions.markerGlobalSelectDamagePrompt')}
                            </div>
                            <div className="camera-damage-actions">
                                <button className="camera-damage-button" onClick={() => this.openDamageModeFromMarkerFlow('new')}>
                                    {t('cameraPositions.createNewDamage')}
                                </button>
                                {this.state.markerDamagePromptType !== 'noDamage' && (
                                    <button className="camera-damage-button" onClick={() => this.openDamageModeFromMarkerFlow('select')}>
                                        {t('cameraPositions.associateDamage')}
                                    </button>
                                )}
                                <button className="camera-damage-button" onClick={this.closeMarkerPrompt}>{t('markers.cancel')}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>;
    }
}

export default (observer: Observer) => {
    // render out the app
    ReactDOM.render(
        <App observer={observer}/>,
        document.getElementById('app')
    );
};

